import { selectFeatureFlags } from 'core/userContext';
import { useCallback, useState } from 'react';
import { SubmitHandler, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { WebLink2, body2, spacing } from 'ui/theme';
import { AlertsModalType, useAlertsModalContext } from '../../context';
import { useUpdateAlerts } from '../../hooks';
import { AlertsFormState } from '../../types';
import { AlertBulkUpdateSettings, BulkUpdateOption } from './AlertBulkUpdateSettings';
import { UpdatedAlertsSummary } from './UpdatedAlertsSummary';

const StyledModalBody = styled(ModalBody)`
  display: flex;
  flex-direction: column;
  gap: ${spacing.base};
  ${body2};
`;

export const UPDATE_ALERT_MODAL_ID = 'update-alert-modal';
export const SAVE_ALERT_BUTTON_ID = 'save-alert-button';
export const LEARN_MORE_LINK_ID = 'learn-more-link-id';

export const UpdateAlertsModal = () => {
  const { CampaignAlertBulkUpdates: isCampaignAlertBulkUpdatesEnabled } =
    useSelector(selectFeatureFlags);
  const { handleSubmit } = useFormContext<AlertsFormState>();
  const { closeModal } = useAlertsModalContext();
  const { updateAlerts, isLoading } = useUpdateAlerts();

  const [bulkUpdateSetting, setBulkUpdateSetting] = useState<BulkUpdateOption>(
    BulkUpdateOption.OnlyFutureCampaigns,
  );

  const handleUpdate: SubmitHandler<AlertsFormState> = useCallback(
    (values) => updateAlerts(values, bulkUpdateSetting),
    [bulkUpdateSetting, updateAlerts],
  );

  return (
    <Modal
      data-test={UPDATE_ALERT_MODAL_ID}
      withPortal
      isModalOpen
      onClose={() => closeModal(AlertsModalType.UpdateAlertsModal)}
      id={UPDATE_ALERT_MODAL_ID}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.WARNING}
    >
      <ModalHeader>Update alert settings?</ModalHeader>
      <StyledModalBody>
        {!isCampaignAlertBulkUpdatesEnabled && (
          <div>
            Changes will apply to any new or existing campaigns with a draft or ready status and
            alerts enabled.{' '}
            <WebLink2
              data-test={LEARN_MORE_LINK_ID}
              href="https://support.iterable.com/hc/articles/19437186634772"
              rel="noreferrer noopener"
              target="_blank"
            >
              Learn more
            </WebLink2>
          </div>
        )}
        {isCampaignAlertBulkUpdatesEnabled && (
          <AlertBulkUpdateSettings
            bulkUpdateSetting={bulkUpdateSetting}
            onBulkUpdateChange={setBulkUpdateSetting}
          />
        )}
        <UpdatedAlertsSummary />
      </StyledModalBody>
      <ModalFooter>
        <PrimaryButton
          data-test={SAVE_ALERT_BUTTON_ID}
          isLoading={isLoading}
          onClick={handleSubmit(handleUpdate)}
          size={BUTTON_SIZES.LARGE}
        >
          Save changes
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
